import { Type } from '@sinclair/typebox';
import { literalUnion } from './union.js';
/**
 * Stable machine-readable error codes.
 *
 * The API never sends a user-facing sentence. It sends a code plus optional
 * interpolation params, and the client renders it through vue-i18n in the
 * user's language. Doing it the other way round would mean the server has to
 * know the caller's locale, and every native client would be stuck with
 * whatever wording the server chose.
 *
 * Codes are permanent contract: rename one and you break every client that
 * translates it. Add new codes rather than repurposing old ones.
 */
export const ERROR_CODES = [
    'validation.failed',
    'auth.unauthenticated',
    'auth.invalid_credentials',
    'auth.email_not_verified',
    'auth.totp_required',
    'auth.totp_invalid',
    'auth.totp_enrolment_required',
    'auth.account_locked',
    // Signing into an account whose deletion has been asked for, in the hour
    // before it is erased. Its own code rather than `account_locked`, because
    // the way out is different and the sign-in screen has to say which: the
    // link in the email keeps the account, and nothing an administrator does
    // will. Answered only after the password has been checked, like a lock, so
    // it tells nobody without the password anything.
    'auth.account_deletion_scheduled',
    'auth.session_expired',
    'auth.forbidden',
    'auth.reauthentication_required',
    'resource.not_found',
    'resource.conflict',
    'resource.gone',
    'upload.duplicate',
    'upload.too_large',
    'upload.unsupported_type',
    // Read successfully and there was nothing in it. Overwhelmingly a scanned PDF:
    // a picture of a page with no text layer, which looks entirely normal to the
    // person who uploaded it. Distinct from `unsupported_type`, which means we
    // never opened it - one says OCR this first, the other says send something
    // else.
    'upload.no_text',
    // Completion was asked for while parts are still missing. Distinct from
    // `resource.conflict` because it is not a dead end: the client is told which
    // parts to send and the same session finishes normally. Telling it only that
    // something conflicted would make a resumable upload look like a failed one.
    'upload.incomplete',
    'quota.exceeded',
    'rate_limit.exceeded',
    // The account has no API key for the provider it is trying to use. Distinct
    // from `upstream.invalid_credentials`, which means a key exists and was
    // refused: one sends the user to the settings screen to paste a key, the
    // other to replace the one that stopped working.
    'credentials.missing',
    // Too many replies are streaming at once, for everybody, not for this user.
    // Not `rate_limit.exceeded`, which is about how fast one caller is asking:
    // this is a shared ceiling, nobody did anything wrong, and waiting a moment
    // genuinely fixes it. See STREAM_CAPACITY in the chat API for why it exists.
    'stream.capacity_exceeded',
    'upstream.unavailable',
    'upstream.invalid_credentials',
    // The provider refused to generate, rather than failing to. Its own decision,
    // so retrying the identical prompt will not help.
    'upstream.content_filtered',
    // Several attempts produced no answer at all - typically a reasoning model
    // that spends its whole output budget thinking. Distinct from a failure
    // because nothing went wrong: the model simply never got to the reply, and a
    // resumed stream cannot continue from thinking it is not allowed to be given
    // back. Asking again in fewer words, or on another model, is what helps.
    'upstream.no_answer',
    // The model is alive and well; this *key* may not use it. The provider answers
    // 404 with "Not found for account '…'", which is a fact about one credential
    // rather than about the catalogue.
    //
    // Distinct from `resource.gone` for a reason that was a live bug: both used to
    // map to it, and `resource.gone` retires the model for **everybody**. One user
    // whose key lacked an entitlement therefore deleted a working model from every
    // other user's picker. A model nobody on this deployment can reach is still a
    // model somebody else can.
    'upstream.model_unavailable',
    /**
     * The object store is unreachable - Backblaze in production, MinIO locally.
     *
     * Its own code rather than `server.internal`, because the two call for
     * different sentences and different behaviour. An internal error means
     * something here is broken and a person can do nothing but report it; this
     * means a third party is having a bad minute, the photographs are safe, and
     * trying again shortly is the right response - which is what the gallery now
     * says, with a button.
     *
     * 503 with the same reasoning `/health` applies: the release is fine, the
     * media is not.
     */
    'storage.unavailable',
    /**
     * The app that sent this request is older than the oldest one the server
     * still answers: its `X-HB-App` header (see `app-version.ts`) names a build
     * below the minimum. Sent with **426 Upgrade Required**, whatever the route.
     *
     * **The one answer that can never change.** Every other code can be added to,
     * and old apps survive; this one is how an old app learns it has to be
     * replaced, so every version ever shipped reads it exactly as it reads it
     * today - status 426 and this code. The Android app keeps its session when it
     * sees it, shows its own update screen, stops backing up and says so in a
     * notification.
     */
    'app.update_required',
    'server.internal',
];
/** One invalid field, so a form can highlight the exact input that failed. */
export const FieldErrorSchema = Type.Object({
    path: Type.String(),
    code: Type.String(),
    message: Type.String(),
});
export const ApiErrorSchema = Type.Object({
    error: Type.Object({
        code: literalUnion(ERROR_CODES),
        /** English text for logs and developers - clients display the translated `code`. */
        message: Type.String(),
        /** Interpolation values for the client-side translation, e.g. `{ limit: 50 }`. */
        params: Type.Optional(Type.Record(Type.String(), Type.Union([Type.String(), Type.Number(), Type.Boolean()]))),
        fields: Type.Optional(Type.Array(FieldErrorSchema)),
        /** Correlates a user-visible failure with the server logs. */
        requestId: Type.Optional(Type.String()),
    }),
});
/** HTTP status for each code, so no route has to remember the mapping. */
const STATUS_BY_CODE = {
    'validation.failed': 400,
    'auth.unauthenticated': 401,
    'auth.invalid_credentials': 401,
    'auth.email_not_verified': 403,
    'auth.totp_required': 401,
    'auth.totp_invalid': 401,
    'auth.totp_enrolment_required': 403,
    'auth.account_locked': 403,
    'auth.account_deletion_scheduled': 403,
    'auth.session_expired': 401,
    'auth.forbidden': 403,
    'auth.reauthentication_required': 403,
    'resource.not_found': 404,
    'resource.conflict': 409,
    'resource.gone': 410,
    'upload.duplicate': 409,
    'upload.too_large': 413,
    'upload.unsupported_type': 415,
    'upload.no_text': 422,
    'upload.incomplete': 409,
    'quota.exceeded': 402,
    'rate_limit.exceeded': 429,
    'credentials.missing': 403,
    // 503 with a `Retry-After`, not 429: the caller is not being throttled, the
    // service is temporarily full, and the difference decides whether a client
    // backs off its own requests or simply tries again.
    'stream.capacity_exceeded': 503,
    'upstream.unavailable': 502,
    'upstream.invalid_credentials': 502,
    'upstream.content_filtered': 422,
    // 502 rather than 422: the request was fine and the provider is the reason
    // there is no answer, which is the same shape as `upstream.unavailable`.
    'upstream.no_answer': 502,
    // 403 rather than 404 or 502. Nothing is missing and nothing is broken: the
    // model exists, the provider is healthy, and this account is not allowed to
    // use that one. 404 would say the model is gone - which is the confusion this
    // code was split out of `resource.gone` to end.
    'upstream.model_unavailable': 403,
    'storage.unavailable': 503,
    'app.update_required': 426,
    'server.internal': 500,
};
export function statusForErrorCode(code) {
    return STATUS_BY_CODE[code];
}
/**
 * The single error type the API throws. A central handler turns it into the
 * wire shape above, so no route builds an error response by hand.
 */
export class AppError extends Error {
    code;
    status;
    params;
    fields;
    constructor(code, options = {}) {
        super(options.message ?? code, options.cause === undefined ? undefined : { cause: options.cause });
        this.name = 'AppError';
        this.code = code;
        this.status = statusForErrorCode(code);
        this.params = options.params;
        this.fields = options.fields;
    }
    toResponse(requestId) {
        return {
            error: {
                code: this.code,
                message: this.message,
                ...(this.params ? { params: this.params } : {}),
                ...(this.fields ? { fields: this.fields } : {}),
                ...(requestId ? { requestId } : {}),
            },
        };
    }
}
export function isAppError(value) {
    return value instanceof AppError;
}
//# sourceMappingURL=errors.js.map